import { ChangeEvent } from 'react';

import {
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    Stack,
    Typography,
} from '@mui/material';
import { FormikErrors } from 'formik';
import { UKBankDetails } from 'types/graphql';

import MainCard from 'src/components/MainCard/MainCard';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import { isApplicationEditable } from 'src/pages/MerchantApplicationPage/merchantApplicationHelpers.util';

import GBBankFields, { BankField, BankFieldsValues } from './GBBankFields';

type GBBankDetailsFormProps = {
    values: {
        settlementAccountSameAsChargeable: boolean;
        settlementAccountSortCode: string;
        settlementAccountAccountNumber: string;
        settlementAccountAccountName: string;
        settlementAccountBankName: string;
        settlementAccountBranch: string;
        settlementAccountAddressLine1: string;
        settlementAccountAddressLine2?: string;
        settlementAccountTown: string;
        settlementAccountPostCode: string;
        chargeableAccountSortCode: string;
        chargeableAccountAccountNumber: string;
        chargeableAccountAccountName: string;
        chargeableAccountBankName: string;
        chargeableAccountBranch: string;
        chargeableAccountAddressLine1: string;
        chargeableAccountAddressLine2?: string;
        chargeableAccountTown: string;
        chargeableAccountPostCode: string;
    };
    errors: FormikErrors<{ [key: string]: string | undefined }>;
    handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
    setFieldValue: (field: string, value: string) => void;
    setValues: (values: BankFieldsValues) => void;
    setUKBankDetailsResult: (result: UKBankDetails) => void;
    setValidatingBankDetails: (validating: boolean) => void;
    bankValidationResult?: UKBankDetails;
};

const GBBankDetailsForm = ({
    values,
    handleChange,
    errors,
    setFieldValue,
    setValues,
    setUKBankDetailsResult,
    setValidatingBankDetails,
    bankValidationResult,
}: GBBankDetailsFormProps) => {
    const { merchantApplication } = useAddMerchantContext();
    const isEditingDisabled = isApplicationEditable(merchantApplication);

    const sharedBankFieldProps: Record<string, { label: string }> = {
        accountName: {
            label: 'Account Name',
        },
        bankName: {
            label: 'Bank Name',
        },
        branch: {
            label: 'Branch',
        },
        addressLine1: {
            label: 'Address Line 1',
        },
        addressLine2: {
            label: 'Address Line 2',
        },
        town: {
            label: 'Town',
        },
        postCode: {
            label: 'Postcode',
        },
    };

    const settlementBankFields: Record<string, BankField> = {
        sortCode: {
            name: 'settlementAccountSortCode',
            chargeableField: 'chargeableAccountSortCode',
        },
        accountNumber: {
            name: 'settlementAccountAccountNumber',
            chargeableField: 'chargeableAccountAccountNumber',
        },
        accountName: {
            name: 'settlementAccountAccountName',
            ...sharedBankFieldProps.accountName,
            chargeableField: 'chargeableAccountAccountName',
        },
        bankName: {
            name: 'settlementAccountBankName',
            ...sharedBankFieldProps.bankName,
            chargeableField: 'chargeableAccountBankName',
        },
        branch: {
            name: 'settlementAccountBranch',
            ...sharedBankFieldProps.branch,
            chargeableField: 'chargeableAccountBranch',
        },
        addressLine1: {
            name: 'settlementAccountAddressLine1',
            ...sharedBankFieldProps.addressLine1,
            chargeableField: 'chargeableAccountAddressLine1',
        },
        addressLine2: {
            name: 'settlementAccountAddressLine2',
            ...sharedBankFieldProps.addressLine2,
            chargeableField: 'chargeableAccountAddressLine2',
        },
        town: {
            name: 'settlementAccountTown',
            ...sharedBankFieldProps.town,
            chargeableField: 'chargeableAccountTown',
        },
        postCode: {
            name: 'settlementAccountPostCode',
            ...sharedBankFieldProps.postCode,
            chargeableField: 'chargeableAccountPostCode',
        },
    };

    const chargeableBankFields: Record<string, BankField> = {
        sortCode: {
            name: 'chargeableAccountSortCode',
        },
        accountNumber: {
            name: 'chargeableAccountAccountNumber',
        },
        accountName: {
            name: 'chargeableAccountAccountName',
            ...sharedBankFieldProps.accountName,
        },
        bankName: {
            name: 'chargeableAccountBankName',
            ...sharedBankFieldProps.bankName,
        },
        branch: {
            name: 'chargeableAccountBranch',
            ...sharedBankFieldProps.branch,
        },
        addressLine1: {
            name: 'chargeableAccountAddressLine1',
            ...sharedBankFieldProps.addressLine1,
        },
        addressLine2: {
            name: 'chargeableAccountAddressLine2',
            ...sharedBankFieldProps.addressLine2,
        },
        town: {
            name: 'chargeableAccountTown',
            ...sharedBankFieldProps.town,
        },
        postCode: {
            name: 'chargeableAccountPostCode',
            ...sharedBankFieldProps.postCode,
        },
    };

    return (
        <MainCard>
            <Typography fontSize="24px" color="#575757" marginBottom="8px">
                Bank Details
            </Typography>
            <Divider
                sx={{
                    borderColor: 'warning.main',
                    marginBottom: '34px',
                }}
            />
            <Stack direction="row" spacing={2} alignItems="center">
                <FormLabel id="settlementAccountSameAsChargeable" sx={{ color: '#575757' }}>
                    Are Settlement Accounts (money paid in) the same as chargeable accounts?
                </FormLabel>
                <FormControl>
                    <RadioGroup
                        aria-labelledby="settlementAccountSameAsChargeable"
                        value={values.settlementAccountSameAsChargeable}
                        name="settlementAccountSameAsChargeable"
                        row
                        onChange={(event) => {
                            setFieldValue(
                                'settlementAccountSameAsChargeable',
                                JSON.parse(event.target.value)
                            );
                        }}
                    >
                        <FormControlLabel
                            value
                            control={<Radio />}
                            label="Yes"
                            disabled={isEditingDisabled}
                        />
                        <FormControlLabel
                            value={false}
                            control={<Radio />}
                            label="No"
                            disabled={isEditingDisabled}
                        />
                    </RadioGroup>
                </FormControl>
            </Stack>
            <Typography color="#8E8E8D" marginTop="16px">
                A separate proof of bank will be needed for the above bank details. Please make sure
                you upload this in the supporting documents step.
            </Typography>

            <GBBankFields
                heading={
                    values.settlementAccountSameAsChargeable
                        ? 'Settlement Account & Chargeable Account'
                        : 'Settlement Account'
                }
                type="settlement"
                bankFields={settlementBankFields}
                values={values}
                errors={errors}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
                setValues={setValues}
                setBankValidationResult={(res) => {
                    setUKBankDetailsResult(res);
                    setValidatingBankDetails(false);
                }}
                setValidatingBankDetails={setValidatingBankDetails}
                settlementAccountSameAsChargeable={values.settlementAccountSameAsChargeable}
                isEditingDisabled={isEditingDisabled}
                bankValidationResult={bankValidationResult}
            />

            {!values.settlementAccountSameAsChargeable && (
                <GBBankFields
                    heading="Chargeable Account"
                    type="chargeable"
                    bankFields={chargeableBankFields}
                    values={values}
                    errors={errors}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    setValues={setValues}
                    setBankValidationResult={(res) => {
                        setUKBankDetailsResult(res);
                        setValidatingBankDetails(false);
                    }}
                    setValidatingBankDetails={setValidatingBankDetails}
                    isEditingDisabled={isEditingDisabled}
                    bankValidationResult={bankValidationResult}
                />
            )}
        </MainCard>
    );
};

export default GBBankDetailsForm;
